import {
  IonContent,
  IonIcon,
  IonPage,
  IonRefresher,
  IonRefresherContent,
  type RefresherEventDetail,
  useIonRouter,
} from "@ionic/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useParams } from "react-router-dom";
import { useStore } from "@tanstack/react-form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { helpCircleOutline } from "ionicons/icons";
import { toast } from "sonner";
import { ModalSheetAutoHeight } from "@/components/modal/sheet/auto-height";
import { ApplicationConfirmationItem } from "@/components/molecules/application/application-confirmation-item";
import { ApplicationCustomFields } from "@/components/molecules/application/application-custom-fields";
import { ApplicationSignupContainer } from "@/components/molecules/application/application-signup.container";
import { ApplicationsModal } from "@/components/molecules/application/applications-modal";
import { InfoModal } from "@/components/molecules/info-modal";
import { ProjectHeader } from "@/components/molecules/project/project-header";
import { ProjectHeroImage } from "@/components/molecules/project/project-hero-image";
import { ProjectInfo } from "@/components/molecules/project/project-info";
import { useBoomerForm } from "@/form/boomer/hook";
import { api } from "@/lib/backend";
import { extractionWrapper } from "@/lib/backend/utils/extract";
import { useRevokePeriod } from "@/lib/backend/utils/hooks/use-revoke-period";
import { useApplicationInfoSections } from "@/lib/constants/application-info";
import {
  buildCustomFieldValueInput,
  hasPendingFileRead,
  isEmptyCustomFieldValue,
  isPendingFileValue,
} from "@/lib/custom-fields/build-value-input";
import { useMe } from "@/lib/hooks/use-me";
import { useApplications } from "@/lib/query/application/use-applications-for-project";
import { useProjectSignupSlots } from "@/lib/query/availability-slot/use-project-signup-slots";
import { useShiftsForProject } from "@/lib/query/shift/use-shifts-for-project";
import { Routes } from "@/lib/router";
import { buildSignupDateGroups } from "@/lib/signup/date-groups";
import { useProjectSelectionStore } from "@/lib/store/project-selection";
import { openFile } from "@/lib/utils/download";
import {
  getShiftTerminology,
  getSignupNextStepText,
  getSignupSuccessMessage,
  getSignupTitle,
} from "@/lib/utils/scheduling-text";
import { logger } from "@/logger";
import { dayKeyToDate, formatDateBox } from "@/utils/format-datetime";
import { getSlotRoleLabel } from "@/utils/functions/get-slot-role-label";
import { useIonicQueryParam } from "@/utils/hooks/use-ionic-query-param";
import { getUtcStartOfDay } from "@/utils/time";

/**
 * De hero is 16:9, net als elk ander kader dat een projectafbeelding toont (app#785).
 *
 * Hij stond op een vaste 300px over de volle breedte. Dat is geen vaste verhouding: op een telefoon
 * van 393pt las het als ~1,3:1 tegen een bestand van 16:9, dus sneed `object-cover` er links en
 * rechts samen een kwart af. Op een tablet weer heel anders. Dit was het enige kader dat nog echt
 * bijsneed, en daarmee de enige reden dat het focuspunt bestond.
 *
 * `56.25vw` is 9/16 van de schermbreedte. De hero wordt daarmee op een telefoon ~80px korter dan
 * voorheen en op een tablet juist hoger — maar altijd precies de afbeelding, nooit een uitsnede.
 */
const HERO_IMAGE_HEIGHT = "56.25vw";

/**
 * Hoeveel de witte inhoudskaart óver de afbeelding heen schuift. Stond als absolute positie (250 op
 * een hero van 300); als overlap blijft hij kloppen nu de hoogte met het scherm meebeweegt.
 */
const CONTENT_OVERLAP_PX = 50;

export function ProjectLayout() {
  const { t } = useTranslation(["custom-project-detail"]);
  const { id } = useParams<{ id: string }>();
  const locationObj = useLocation();
  const location = "action" in locationObj ? locationObj.location : locationObj;
  const router = useIonRouter();
  const fromUrl = useIonicQueryParam("fromUrl");
  // Optional date (YYYY-MM-DD) of the tapped "ontdek" card, so we open on that
  // date's signup segment instead of the first one.
  const initialDay = useIonicQueryParam("day") ?? undefined;
  const queryClient = useQueryClient();
  const { setHasActiveSelection, reset: resetSelectionStore } = useProjectSelectionStore();
  const { getRevokePeriodText, revokePeriod, revokeNotice } = useRevokePeriod();

  // Determine the back URL based on origin parameter
  const origin = Routes.Project.useSearchParams().origin;
  const routeBackUrl =
    origin === "upcoming" ? Routes.UpcomingShifts() : (fromUrl ?? Routes.Projects());

  const handleRefresh = async (event: CustomEvent<RefresherEventDetail>) => {
    try {
      // Invalidate and refetch all queries related to this project
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["project", id] }),
        queryClient.invalidateQueries({ queryKey: ["documents", id] }),
        queryClient.invalidateQueries({ queryKey: ["shifts", id] }),
        queryClient.invalidateQueries({ queryKey: ["additionalFields", id] }),
        queryClient.invalidateQueries({ queryKey: ["availabilitySlots", "forUserFunction", id] }),
        queryClient.invalidateQueries({
          queryKey: ["availabilitySlots", "withoutUserFunction", id],
        }),
        queryClient.invalidateQueries({ queryKey: ["applications", id] }),
      ]);
    } catch (error) {
      logger.error("Failed to refresh project data:", error);
    } finally {
      event.detail.complete();
    }
  };

  const { data: project } = useQuery({
    queryKey: ["project", id],
    queryFn: async () => {
      const res = await api.project.get(id);
      return res.data;
    },
  });

  const applicationInfoSections = useApplicationInfoSections(
    getRevokePeriodText,
    revokePeriod,
    project?.schedulingMethod,
  );

  // Fetch additional fields for the project
  const { data: additionalFields } = useQuery({
    queryKey: ["additionalFields", id],
    queryFn: async () => {
      const res = await api.additionalField.getAll(id);
      return res.data;
    },
    enabled: !!id,
  });

  // De hero haalt zijn eigen ondertekende URL op via ProjectImage — zelfde
  // query-sleutel, dus dit kost geen extra verzoek.
  const { data: organization } = useQuery({
    queryKey: ["organization"],
    queryFn: () => {
      return extractionWrapper(api.organization.getMyOrganization());
    },
  });

  const { data: shifts } = useShiftsForProject(id);
  const { me } = useMe();

  const { data: applications } = useApplications(id);

  // Which open slots this employee may sign up for. Extracted to `lib/query/availability-slot` so
  // Ontdek's signup sheet asks the exact same question — see `useProjectSignupSlots`.
  const availabilitySlots = useProjectSignupSlots(id);

  const [selectedApplicationIds, setSelectedApplicationIds] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // `isSubmitting` is state, and state does not exist yet for the tap that arrives in the same
  // frame as the first one — `disabled` only takes effect after React re-renders. A ref closes
  // that window, so two fast taps cannot both post an application and both dismiss the sheet.
  const isSubmittingRef = useRef(false);
  const modalConfirmation = useRef<HTMLIonModalElement>(null);
  const modalInfo = useRef<HTMLIonModalElement>(null);
  const modalConfirmationInfo = useRef<HTMLIonModalElement>(null);
  const modalApplications = useRef<HTMLIonModalElement>(null);

  // Custom fields form for application
  type CustomFieldFormValue = Record<string, any>;
  const [customFieldsForm, { Element: CustomFieldsFormElement }] = useBoomerForm<
    CustomFieldFormValue,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined
  >({
    defaultValues: {},
    onSubmit: async () => {
      // Form submission is handled in the submit button below
    },
  });

  // True from the moment a file is picked until `FileReader` hands back the data-URL. The submit
  // button blocks on this, so the read cannot still be running when the handler starts (#117).
  const isReadingFile = useStore(customFieldsForm.store, (state) =>
    hasPendingFileRead(state.values as Record<string, unknown>),
  );

  // Revoke application mutation
  const revokeApplicationMutation = useMutation({
    mutationKey: ["application", "revoke"],
    mutationFn: (applicationId: string) =>
      api.application.revokeEmployeeApplication({ id: applicationId }),
    onSuccess: () => {
      toast.success(t("actions:revoke.success"));
      queryClient.invalidateQueries({ queryKey: ["applications", id] });
      queryClient.invalidateQueries({ queryKey: ["availabilitySlots", id] });
    },
    onError: (error) => {
      logger.error("Failed to revoke application:", error);
      toast.error(t("actions:revoke.error"));
    },
  });

  // Which days this employee can still sign up for. Extracted to `lib/signup/date-groups` so
  // Ontdek can ask the same question — this used to be the only place in the app that knew.
  const dateGroups = useMemo(
    () => buildSignupDateGroups(availabilitySlots?.items, applications?.items),
    [availabilitySlots, applications],
  );

  // Track if we need to show the floating action button (has new selections different from existing applications)
  const [hasInitializedApplications, setHasInitializedApplications] = useState(false);

  // Mark as initialized once we have applications data (but don't pre-select anything)
  useEffect(() => {
    if (applications?.items && !hasInitializedApplications) {
      setHasInitializedApplications(true);
    }
  }, [applications, hasInitializedApplications]);

  const { data: documents } = useQuery({
    queryKey: ["documents", id],
    queryFn: () => extractionWrapper(api.document.getAll("project", id)),
    enabled: !!id && !!shifts?.items?.length,
  });

  // Prepare action buttons for ProjectInfo component
  const actionButtons = useMemo(() => {
    const buttons = [];

    // Aanmeldingen button
    if (applications?.items && applications.items.length > 0) {
      buttons.push({
        key: "aanmeldingen",
        label: "Aanmeldingen",
        badge: applications.items.length,
        onClick: () => {
          modalApplications.current?.present();
          logger.info("Open applications modal");
        },
      });
    }

    // Document buttons
    if (documents?.items) {
      documents.items.forEach((document) => {
        buttons.push({
          key: document.id,
          label: document.name,
          onClick: async () => {
            try {
              const encodedDocumentName = encodeURIComponent(document.name);
              const response = await api.file.getProjectDocumentUrl(id!, encodedDocumentName);
              openFile(response.data.url);
            } catch (err) {
              logger.error("File download failed:", err);
              toast.error(t("common:applications.download-failed"));
            }
          },
        });
      });
    }

    return buttons;
  }, [applications, documents, router, id]);

  // Sync selection state with Zustand store for tab bar visibility
  useEffect(() => {
    if (!hasInitializedApplications) {
      return;
    }

    // Check if there are new selections different from existing applications
    const appliedSlotIds =
      applications?.items
        ?.filter(
          (app) =>
            app.status === "ACCEPTED" || app.status === "PENDING" || app.status === "RESERVE",
        )
        .map((app) => app.availabilitySlotId as unknown as string)
        .filter(Boolean) ?? [];

    // Compare both arrays to see if they're different
    // Check if lengths differ OR if any selected ID is not in applied IDs OR vice versa
    const hasNewSelections =
      selectedApplicationIds.length !== appliedSlotIds.length ||
      selectedApplicationIds.some((id) => !appliedSlotIds.includes(id)) ||
      appliedSlotIds.some((id) => !selectedApplicationIds.includes(id));

    setHasActiveSelection(hasNewSelections);
  }, [selectedApplicationIds, applications, hasInitializedApplications, setHasActiveSelection]);

  // Reset store on unmount
  useEffect(() => {
    return () => {
      resetSelectionStore();
    };
  }, [resetSelectionStore]);

  return (
    <IonPage>
      <IonContent
        fullscreen
        scrollY={true}
        // Make sure the content background is plain white so the fade blends into it
        style={{
          "--background": "white",
        }}
      >
        <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
          <IonRefresherContent refreshingSpinner="circles" />
        </IonRefresher>
        {/* Fixed background image with header buttons */}
        <ProjectHeroImage
          projectId={id}
          imageKey={project?.image}
          alt={project?.name || "Project"}
          height={HERO_IMAGE_HEIGHT}
        >
          <ProjectHeader
            projectName={project?.name}
            projectId={id}
            organizationSlug={organization?.slug}
            backendUrl={import.meta.env.VITE_BACKEND_URL}
            backUrl={routeBackUrl}
          />
        </ProjectHeroImage>

        {/* Scrollable content container - slides over the fixed image */}
        <div
          className="relative z-10"
          style={{ marginTop: `calc(${HERO_IMAGE_HEIGHT} - ${CONTENT_OVERLAP_PX}px)` }}
        >
          {/* Content card that slides over the image */}
          <div className="relative bg-white">
            {/* Gradient shadow at top for smooth transition - extends above the card */}
            <div
              className="pointer-events-none absolute inset-x-0 -top-8 h-8"
              style={{
                backgroundImage:
                  "linear-gradient(0deg, rgba(255,255,255,1) 0%, rgba(255,255,255,0.0) 100%)",
              }}
            />
            {/* Project info: title, actions, location, description */}
            <ProjectInfo
              projectId={id}
              name={project?.name || ""}
              description={project?.description ?? undefined}
              addressLine={project?.addressLine ?? undefined}
              city={project?.city ?? undefined}
              zipCode={project?.zipCode ?? undefined}
              state={project?.state ?? undefined}
              actionButtons={actionButtons}
              additionalFields={additionalFields}
              whatsappGroupLink={
                shifts?.items?.length ? (project?.whatsappGroupLink ?? undefined) : undefined
              }
            />

            {/* Content */}
            <div className="px-4">
              {/* Application signup section - show if user has applications */}
              {dateGroups.length > 0 && (
                <div className="mt-8">
                  <ApplicationSignupContainer
                    dateGroups={dateGroups}
                    selectedShiftIds={selectedApplicationIds}
                    onSelectionChange={(slotIds) => {
                      setSelectedApplicationIds(slotIds);
                    }}
                    onHelpClick={() => {
                      modalInfo.current?.present();
                    }}
                    schedulingMethod={project?.schedulingMethod}
                    initialDate={initialDay}
                  />
                </div>
              )}

              {/* Shifts section
              <div className="mt-8 flex flex-col gap-4">
                <section>
                  <Heading order={2}>{t("common:applications.deadline.title")}</Heading>
                  <p className="text-txt-secondary">
                    {t("common:applications.deadline.description", {
                      revokePeriodText: getRevokePeriodText(),
                    })}
                  </p>
                </section>

                <section>
                  <Heading order={2}>{t("routes:projects.shifts.title")}</Heading>
                  <div className="flex flex-col gap-4">
                    {!combinedShiftsAndApplications.length && (
                      <p className="text-txt-secondary">{t("common:shifts.no-shifts")}</p>
                    )}
                    {combinedShiftsAndApplications.map((item) =>
                      item.type === "shift" ? (
                        <ShiftCardSimple key={`shift-${item.data.id}`} shift={item.data} />
                      ) : (
                        <ApplicationCardSimple
                          key={`application-${item.data.id}`}
                          application={item.data}
                        />
                      ),
                    )}
                  </div>
                </section>
              </div> */}
            </div>
          </div>
        </div>

        {/* Submit button trigger - only show when shifts are newly selected (not pre-existing applications) */}
        {dateGroups.length > 0 &&
          selectedApplicationIds.length > 0 &&
          hasInitializedApplications &&
          (() => {
            // Only show if selection differs from existing applications
            const appliedSlotIds =
              applications?.items
                ?.filter(
                  (app) =>
                    app.status === "ACCEPTED" ||
                    app.status === "PENDING" ||
                    app.status === "RESERVE",
                )
                .map((app) => app.availabilitySlotId as unknown as string)
                .filter(Boolean) ?? [];

            const hasNewSelections =
              selectedApplicationIds.some((id) => !appliedSlotIds.includes(id)) ||
              selectedApplicationIds.length !== appliedSlotIds.length;

            return hasNewSelections;
          })() && (
            <div
              slot="fixed"
              className="fixed inset-x-0 bottom-0 z-10 border-t border-bordercolor-primary bg-white px-4 py-3"
              style={{
                paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))",
              }}
            >
              <div className="flex items-center justify-between gap-4">
                {/* Left side - selection info */}
                <div className="flex flex-col gap-0.5">
                  <span className="text-base font-semibold text-txt-primary">
                    {selectedApplicationIds.length}{" "}
                    {getShiftTerminology(
                      t,
                      project?.schedulingMethod,
                      selectedApplicationIds.length,
                    )}
                  </span>
                  <span className="text-xs text-txt-secondary">
                    {dateGroups
                      .filter((group) =>
                        group.shifts.some((shift) => selectedApplicationIds.includes(shift.id)),
                      )
                      .map((group) => group.label)
                      .join(", ")}
                  </span>
                </div>

                {/* Right side - action button */}
                <button
                  type="button"
                  onClick={() => modalConfirmation.current?.present()}
                  className="shrink-0 rounded-lg bg-button-primary px-6 py-3 text-sm font-semibold text-txt-inverted transition-transform active:scale-[0.98]"
                >
                  Ga verder
                </button>
              </div>
            </div>
          )}

        {/* Confirmation Modal */}
        <ModalSheetAutoHeight ref={modalConfirmation} backdropDismiss={true}>
          <div className="flex max-h-[80vh] flex-col gap-6 p-6">
            {/* Header */}
            <div className="flex shrink-0 items-start justify-between">
              <div className="flex flex-col">
                <h2 className="text-xl font-bold text-txt-primary">
                  {t("common:applications.confirm-signup")}
                </h2>
                <p className="mt-1 text-sm text-txt-secondary">
                  {selectedApplicationIds.length}{" "}
                  {getShiftTerminology(t, project?.schedulingMethod, selectedApplicationIds.length)}{" "}
                  geselecteerd
                </p>
              </div>
              <button
                type="button"
                onClick={() => modalConfirmationInfo.current?.present()}
                className="flex h-8 w-8 items-center justify-center"
              >
                <IonIcon icon={helpCircleOutline} className="text-2xl text-txt-tertiary" />
              </button>
            </div>

            {/* Selected shifts list and custom fields - scrollable */}
            <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto">
              {dateGroups
                .filter((group) =>
                  group.shifts.some((shift) => selectedApplicationIds.includes(shift.id)),
                )
                .map((group) => {
                  const selectedShift = group.shifts.find((shift) =>
                    selectedApplicationIds.includes(shift.id),
                  );
                  if (!selectedShift) {
                    return null;
                  }

                  // Parse date for display. `group.date` is een kale dagsleutel, en
                  // `new Date("2026-08-21")` is middernacht UTC — westelijk van Greenwich stond
                  // daar de dag ervóór in het blokje.
                  const dateBox = formatDateBox(dayKeyToDate(group.date, project?.timeZone), {
                    timeZone: project?.timeZone,
                  });

                  return (
                    <ApplicationConfirmationItem
                      key={group.date}
                      dateLabel={group.label}
                      month={dateBox.month}
                      day={dateBox.day}
                      yearOrWeekday={dateBox.caption}
                      title={selectedShift.title}
                      timeRange={selectedShift.timeRange}
                    />
                  );
                })}

              {/* Custom fields form - inside scrollable area */}
              <ApplicationCustomFields projectId={id} form={customFieldsForm} />
            </div>

            {/* Action buttons */}
            <div className="flex shrink-0 flex-col gap-3">
              <button
                type="button"
                onClick={async () => {
                  if (isSubmittingRef.current) {
                    return;
                  }
                  isSubmittingRef.current = true;
                  setIsSubmitting(true);
                  logger.info("Submitting application for slot IDs:", selectedApplicationIds);

                  try {
                    // Fetch custom field definitions first for validation
                    const customFieldDefinitions = await extractionWrapper(
                      api.customFieldDefinitions.getAll("APPLICATION", { projectId: id }),
                    );

                    // Validate all required fields
                    const formValues = customFieldsForm.state.values;
                    let hasValidationErrors = false;
                    let hasPendingFile = false;

                    customFieldDefinitions.forEach((fieldDef) => {
                      const value = formValues[fieldDef.id];

                      // A chosen file that is still being read would drop out of the payload
                      // silently, so say that rather than sending an application without it (#117).
                      const isPending = isPendingFileValue(fieldDef.type, value);
                      const fieldError = isPending
                        ? t("common:applications.file-still-processing")
                        : fieldDef.required && isEmptyCustomFieldValue(fieldDef.type, value)
                          ? t("common:validation.required")
                          : null;

                      if (fieldError) {
                        hasValidationErrors = true;
                        hasPendingFile = hasPendingFile || isPending;
                        const fieldInstance = customFieldsForm.getFieldInfo(fieldDef.id);
                        if (fieldInstance) {
                          customFieldsForm.setFieldMeta(fieldDef.id, {
                            isTouched: true,
                            isBlurred: true,
                            isDirty: true,
                            isValidating: false,
                            isPristine: false,
                            errors: [fieldError],
                            errorMap: {
                              onChange: fieldError,
                            },
                          });
                        }
                      }
                    });

                    if (hasValidationErrors) {
                      toast.error(
                        hasPendingFile
                          ? t("common:applications.file-still-processing")
                          : t("common:applications.fill-required"),
                      );
                      return;
                    }

                    // Build custom fields payload
                    const customFieldsPayload: Record<string, object> = {};

                    customFieldDefinitions.forEach((fieldDef) => {
                      const value = formValues[fieldDef.id];

                      // Skip if no value provided and field is not required
                      if (isEmptyCustomFieldValue(fieldDef.type, value) && !fieldDef.required) {
                        return;
                      }

                      // Build the custom field value input
                      const fieldValueInput = buildCustomFieldValueInput(fieldDef.type, value);

                      // A FILE without a usable base64 value yields an empty payload; posting that
                      // gives a server error instead of a field message, so leave it out (#117).
                      if (Object.keys(fieldValueInput).length === 0) {
                        logger.info("Skipping empty custom field", {
                          fieldId: fieldDef.id,
                          type: fieldDef.type,
                        });
                        return;
                      }

                      customFieldsPayload[fieldDef.id] = fieldValueInput;
                    });

                    // Log only which fields are being sent: a FILE value holds the full base64
                    // content of a personal document (pasfoto, id-bewijs) and must never end up
                    // in the device log (#117).
                    logger.info("Custom fields payload:", {
                      fieldIds: Object.keys(customFieldsPayload),
                    });

                    // Apply to selected slots
                    await api.application.createEmployeeApplication({
                      availabilitySlotIds: selectedApplicationIds,
                      customFields:
                        Object.keys(customFieldsPayload).length > 0
                          ? customFieldsPayload
                          : undefined,
                    });
                    toast.success(getSignupSuccessMessage(t, project));

                    // Refetch applications to update state
                    await queryClient.invalidateQueries({ queryKey: ["applications", id] });
                    await queryClient.invalidateQueries({
                      queryKey: ["availabilitySlots", id],
                    });

                    // Close modal and reset selection
                    modalConfirmation.current?.dismiss();
                    setSelectedApplicationIds([]);

                    // Reset custom fields form
                    customFieldsForm.reset();
                  } catch (error) {
                    logger.error("Failed to submit applications:", error);
                    toast.error(t("common:applications.signup-failed"));
                  } finally {
                    isSubmittingRef.current = false;
                    setIsSubmitting(false);
                  }
                }}
                disabled={isSubmitting || isReadingFile}
                className="w-full rounded-lg bg-button-primary py-3 text-center text-base font-semibold text-txt-inverted transition-opacity disabled:opacity-50"
              >
                {isReadingFile
                  ? t("fields:file.processing")
                  : isSubmitting
                    ? t("common:applications.signing-up")
                    : t("common:applications.confirm-signup")}
              </button>
              <button
                type="button"
                onClick={() => modalConfirmation.current?.dismiss()}
                className="w-full rounded-lg border border-bordercolor-primary bg-white py-3 text-center text-base font-semibold text-txt-primary"
              >
                Annuleren
              </button>
            </div>
          </div>
        </ModalSheetAutoHeight>

        {/* Info Modal */}
        <InfoModal
          modalRef={modalInfo}
          title={getSignupTitle(t, project?.schedulingMethod)}
          sections={applicationInfoSections}
        />

        {/* Confirmation Info Modal */}
        <InfoModal
          modalRef={modalConfirmationInfo}
          title={t("common:applications.confirm-signup")}
          sections={[
            {
              title: t("common:applications.notice"),
              // Dit is het scherm waarop iemand zich net heeft aangemeld, dus juist hier moet de
              // "let op" kloppen: verbiedt het beleid intrekken, dan is de aanmelding meteen vast
              // en is "je kunt tot 48 uur van tevoren terug" precies het verkeerde om mee te geven.
              content: revokeNotice
                ? [revokeNotice]
                : [
                    t("common:applications.revoke-until", {
                      revokePeriodText: getRevokePeriodText(),
                    }),
                    t("common:applications.revoke-after"),
                  ],
            },
            {
              title: t("common:scheduling.what-next"),
              content: getSignupNextStepText(t, project),
            },
          ]}
        />

        {/* Applications Modal */}
        {applications?.items && applications.items.length > 0 && (
          <ApplicationsModal
            modalRef={modalApplications}
            applications={applications.items}
            onCancelApplication={(applicationId) => {
              revokeApplicationMutation.mutate(applicationId);
            }}
            schedulingMethod={project?.schedulingMethod}
          />
        )}
      </IonContent>
    </IonPage>
  );
}
